import styled from 'styled-components';
import { media, fontSize, fontWeight, colors } from 'settings/style';

export const PageShell = styled.div`
`;

export const PageHeader = styled.div`
  margin-bottom: 14px;
  padding: 2.25rem 0 1.5rem;
  ${media.md`
    padding: 1.5rem 0 1.25rem;
  `}
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-weight: 500;
  color: #1a2135;
`;

export const ChatViewScope = styled.div`
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

export const ChatLayoutGrid = styled.div`
  border: 1px solid ${colors.grey300};
  border-radius: 10px;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  height: 70vh;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const ChatMain = styled.main<{ $visibleOnMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 800px) {
    display: ${(props) => (props.$visibleOnMobile ? 'flex' : 'none')};
  }
`;

export const ChatSidebar = styled.aside<{ $visibleOnMobile?: boolean }>`
  border-right: 1px solid ${colors.grey300};
  display: flex;
  flex-direction: column;
  min-height: 0;
  color: #333;

  @media (max-width: 800px) {
    display: ${(props) => (props.$visibleOnMobile === false ? 'none' : 'flex')};
  }
`;

export const ChatSidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.25rem;
  height: 56px;
  box-sizing: border-box;
`;

export const ChatSidebarTitle = styled.h2`
  margin: 0;
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize.base};
`;

export const ChatSidebarSearch = styled.div`
  flex-shrink: 0;
  padding: 0 0.75rem 0.5rem;

  .ant-input-affix-wrapper {
    border-radius: 8px;
    border-color: ${colors.grey300};
    padding: 0.5rem 0.75rem;
  }

  .ant-input {
    font-size: 0.875rem;
    background: transparent;
  }
`;

export const ChatNewChatRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const ChatConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

export const ChatConversationItem = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid ${colors.grey300};
  outline: none;
  cursor: pointer;
  background: ${(props) => (props.$active ? colors.primary300 : 'transparent')};
  color: #666;
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover {
    background: ${(props) => (props.$active ? colors.primary300 : '#c0d8e1')};
  }
`;

export const ChatConversationAvatarWrap = styled.div`
  flex-shrink: 0;
`;

export const ChatConversationBody = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 0.5rem;
  row-gap: 0.2rem;
  align-items: center;
`;

export const ChatConversationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
`;

export const ChatConversationNameRow = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
`;

export const ChatConversationName = styled.span`
  font-size: 0.95rem;
  font-weight: ${fontWeight.bold};
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

export const ChatConversationPreview = styled.div<{ $unread?: boolean }>`
  grid-column: 1;
  grid-row: 2;
  font-size: 0.82rem;
  line-height: 1.35;
  color: ${(props) => (props.$unread ? '#374151' : '#6b7280')};
  font-weight: ${(props) => (props.$unread ? fontWeight.bold : fontWeight.normal)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const ChatConversationTime = styled.span`
  grid-column: 2;
  grid-row: 1;
  justify-self: end;
  align-self: start;
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
  line-height: 1.2;
`;

export const ChatConversationBadgeSlot = styled.div`
  grid-column: 2;
  grid-row: 2;
  justify-self: end;
  align-self: center;
  min-height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ChatConversationGroupIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #5f6b73;
`;

export const ChatDot = styled.span<{ $online: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => (props.$online ? colors.green500 : '#555555')};
`;

export const ChatUnreadBadge = styled.span`
  flex-shrink: 0;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background-color: #5b5b5f;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const ChatEmptyState = styled.div`
  padding: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
`;

export const ChatPanelEmptyState = styled.div`
  flex-shrink: 0;
  height: 56px;
  box-sizing: border-box;
  padding: 0.5rem 1.25rem;
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: ${fontSize.base};
  border-bottom: 1px solid ${colors.grey300};
`;

export const ChatLoadingState = styled.div`
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
`;

export const ChatLoadingText = styled.span`
  font-weight: ${fontWeight.normal};
`;

export const ChatViewPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

export const ChatHeader = styled.header`
  flex-shrink: 0;
  padding: 0.5rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  box-sizing: border-box;
  border-bottom: 1px solid ${colors.grey300};
`;

export const ChatHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

export const ChatBackButton = styled.button`
  display: none;
  border: none;
  color: black;
  padding: 0;
  line-height: 1;
  cursor: pointer;

  @media (max-width: 800px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ChatTitle = styled.div`
  font-weight: 600;
  font-size: ${fontSize.base};
  color: white;
`;

export const ChatOnlineStatus = styled.span<{ $online: boolean }>`
  font-size: 0.75rem;
  color: ${(props) => (props.$online ? colors.green500 : '#9ca3af')};
`;

export const ChatHeaderAvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

export const ChatHeaderOnlineDot = styled.span<{ $online: boolean }>`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid ${colors.gray50};
  background: ${(props) => (props.$online ? colors.green500 : 'transparent')};
`;

export const ChatHeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

export const ChatHeaderDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

export const ChatHeaderName = styled.div`
  font-weight: 600;
  font-size: ${fontSize.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChatHeaderStatusText = styled.span<{ $online: boolean }>`
  font-size: 0.72rem;
  color: ${(props) => (props.$online ? '#4caf50' : '#9ca3af')};
`;

export const ChatMessages = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin: 0px 0px;
`;

export const ChatMessageRow = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 70%;
  margin-left: ${(props) => (props.$mine ? 'auto' : '0')};
  align-items: ${(props) => (props.$mine ? 'flex-end' : 'flex-start')};
`;

export const ChatMessageSender = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.15rem;
`;

export const ChatGroupIncomingRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.45rem;
  max-width: 100%;
`;

export const ChatGroupSenderInitial = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.74rem;
  font-weight: 700;
  color: #fff;
  background: #8c9aa3;
  flex-shrink: 0;
`;

export const ChatMessageBubble = styled.div<{ $mine: boolean }>`
  padding: 0.5rem 0.75rem;
  border-radius: 0.9rem;
  background-color: ${(props) => (props.$mine ? colors.white : colors.grey300)};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  font-size: 0.95rem;
  color: #333;
  border-top-left-radius: ${(props) => (props.$mine ? '18px' : '5px')};
  border-bottom-right-radius: ${(props) => (props.$mine ? '5px' : '0.9rem')};
  border: 1px solid ${colors.grey300};
`;

export const ChatMessageContent = styled.div`
  word-break: break-word;
  white-space: pre-wrap;
`;

export const ChatMessageMeta = styled.div`
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: #888;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
`;

export const ChatMessageTime = styled.span`
  flex-shrink: 0;
  margin-top: 3px;
`;

export const ChatMessageStatus = styled.span<{ $read: boolean }>`
  display: inline-flex;
  align-items: center;
`;

export const ChatComposer = styled.footer`
  border-top: 1px solid ${colors.grey300};
  flex-shrink: 0;
  padding: 0.75rem 1.25rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 0.5rem;

  .ant-input-textarea {
    min-width: 0;
  }

  .ant-input-textarea textarea {
    resize: none;
  }

  ${media.md`
    padding: 0.5rem 0.5rem;
  `}
`;

export const ChatComposerSend = styled.div`
  align-self: end;
  flex-shrink: 0;
`;
